/**
 * Body → Embed (Prototype v15): which frames an article body may hold, and a
 * YouTube or Vimeo link as its player's address. No editor imports, so it can
 * be tested on its own; bodyNodes.ts and ArticleBodyEditor.tsx use it.
 */

// The site's own video player (Cloudflare Stream; the API writes it).
const STREAM = /^https:\/\/customer-[a-z0-9]+\.cloudflarestream\.com\/[a-f0-9]{32}\/iframe/i;
// A YouTube or Vimeo player (Body → Embed, Prototype v15). The public site's
// sanitizer allows exactly these, rebuilt from the video's id.
const YOUTUBE_PLAYER = /^https:\/\/www\.youtube-nocookie\.com\/embed\/[\w-]{11}$/;
const VIMEO_PLAYER = /^https:\/\/player\.vimeo\.com\/video\/\d+$/;

/** A frame the body may hold: the site's own player, or a YouTube / Vimeo one. */
export const isPlayerSrc = (src: string | null) =>
  Boolean(src) && (STREAM.test(src!) || YOUTUBE_PLAYER.test(src!) || VIMEO_PLAYER.test(src!));

/**
 * A YouTube or Vimeo link as its player's address: youtube.com/watch?v=…,
 * youtu.be/…, /shorts/…, /embed/…, vimeo.com/123, player.vimeo.com/video/123.
 * YouTube plays from its privacy-enhanced host (no cookies until played).
 * Null for anything else.
 */
export function embedPlayer(link: string): string | null {
  let u: URL;
  try {
    u = new URL(/^https?:\/\//i.test(link.trim()) ? link.trim() : `https://${link.trim()}`);
  } catch {
    return null;
  }
  const host = u.hostname.toLowerCase().replace(/^(www|m)\./, '');
  const id = (v: string | null | undefined) => (v && /^[\w-]{11}$/.test(v) ? v : null);
  let yt: string | null = null;
  if (host === 'youtu.be') yt = id(u.pathname.slice(1).split('/')[0]);
  else if (['youtube.com', 'youtube-nocookie.com', 'music.youtube.com'].includes(host)) {
    const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([\w-]{11})/);
    yt = id(m ? m[1] : u.searchParams.get('v'));
  }
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt}`;
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const m = u.pathname.match(/^\/(?:video\/)?(\d+)(?:\/|$)/);
    if (m) return `https://player.vimeo.com/video/${m[1]}`;
  }
  return null;
}
